import * as Sentry from '@sentry/react';
import { fetchAuthSession } from 'aws-amplify/auth';
import { useEffect } from 'react';
import {
  createRoutesFromChildren,
  matchRoutes,
  useLocation,
  useNavigationType,
} from 'react-router-dom';

import {
  REACT_APP_ENV,
  REACT_APP_SENTRY_DSN,
  getUserEmail,
  getViewportDimensions,
  hashString,
  isSentryDebugEnabled,
  REACT_APP_ENV_SUFFIX,
} from '#shared';

import {
  customBreadcrumbFilter,
  customErrorFilter,
  rateLimitedErrorFilter,
} from './app';

const SENSITIVE_KEY_DENYLIST = ['forwarded', '-ip', 'remote-', 'via', '-user'];

/**
 * v10's `sendDefaultPii: false` behaviour. Leaving `dataCollection` unset in v11 collects user
 * info, cookies, every header and every HTTP body. Request headers are allowlisted rather than
 * denylisted because the same setting feeds httpClientIntegration, which sent no headers in v10;
 * User-Agent and Referer stay because Sentry's legacy-browser and crawler inbound filters read them.
 */
const V10_DATA_COLLECTION_BASELINE: Sentry.BrowserOptions['dataCollection'] = {
  userInfo: false,
  cookies: false,
  httpHeaders: {
    request: { allow: ['user-agent', 'referer'] },
    response: false,
  },
  httpBodies: [],
  urlQueryParams: { deny: SENSITIVE_KEY_DENYLIST },
  genAI: { inputs: false, outputs: false },
  databaseQueryData: false,
  queues: false,
  graphQL: { document: false, variables: false },
};

const ANON_USER_ID_STORAGE_KEY = 'sentry.user.anon_id.v1';

const getOrCreateAnonymousUserId = (): string | null => {
  try {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    const existing = localStorage.getItem(ANON_USER_ID_STORAGE_KEY);
    if (existing) {
      return existing;
    }

    const created =
      typeof crypto !== 'undefined' && 'randomUUID' in crypto
        ? crypto.randomUUID()
        : `anon_${Math.random().toString(16).slice(2)}_${Date.now()}`;

    localStorage.setItem(ANON_USER_ID_STORAGE_KEY, created);
    return created;
  } catch {
    return null;
  }
};

// Type augmentation for non-standard Performance API
interface MemoryInfo {
  usedJSHeapSize: number;
  totalJSHeapSize: number;
  jsHeapSizeLimit: number;
}

declare global {
  interface Performance {
    memory: MemoryInfo;
  }
}

// Environment detection
const isLocalhost =
  location.hostname === 'localhost' || location.hostname === '127.0.0.1';
const isDevelopment = REACT_APP_ENV === 'development' || isLocalhost;
const isProduction = ['production', 'production-old'].includes(REACT_APP_ENV);
const isStaging = REACT_APP_ENV === 'stage';

// Check debug mode feature flag
const isDebugMode = isSentryDebugEnabled();

// Only initialize Sentry if DSN is provided and not in localhost
if (REACT_APP_SENTRY_DSN && !isLocalhost) {
  Sentry.init({
    dsn: REACT_APP_SENTRY_DSN,

    // Environment configuration
    environment: `${REACT_APP_ENV || 'local'}${REACT_APP_ENV_SUFFIX}`,

    // Debug mode - enabled via feature flag
    debug: isDebugMode,

    // Integrations
    integrations: [
      // Browser tracing for performance monitoring
      Sentry.browserTracingIntegration({
        // Track Core Web Vitals and custom performance metrics
        enableLongTask: true,
        enableInp: true,
        markBackgroundSpan: false,
      }),

      // React Router integration for route tracking
      Sentry.reactRouterBrowserTracingIntegration({
        useEffect,
        useLocation,
        useNavigationType,
        createRoutesFromChildren,
        matchRoutes,
      }),

      // Session replay with time-based configuration
      Sentry.replayIntegration({
        maskAllText: false, // No sensitive data as per requirements
        maskAllInputs: false,
        blockAllMedia: true, // Block videos/audio to reduce payload
        maxReplayDuration: 5_000, // 5 seconds max duration as requested
      }),

      // HTTP monitoring
      Sentry.httpClientIntegration(),

      // Console error capturing
      Sentry.captureConsoleIntegration({
        // Console capture can explode Error volume when code logs expected failures.
        // Keep it stricter in production.
        levels: isProduction ? ['error'] : ['error', 'warn'],
      }),

      // Browser API error monitoring
      Sentry.browserApiErrorsIntegration({
        setTimeout: true,
        setInterval: true,
        requestAnimationFrame: true,
        XMLHttpRequest: true,
        eventTarget: true,
      }),

      // Performance profiling (CPU usage monitoring)
      ...(isProduction ? [] : [Sentry.browserProfilingIntegration()]),

      // Additional context for debugging
      Sentry.extraErrorDataIntegration({
        depth: 3, // Capture 3 levels of nested data
      }),

      // Report browser performance issues
      Sentry.reportingObserverIntegration(),

      // v11 defaults to 'page' (one session per load); 'route' keeps Release Health comparable with v10.
      Sentry.browserSessionIntegration({ lifecycle: 'route' }),
    ],

    // v11 defaults to span streaming, which no longer applies scope tags (initialScope.tags) to spans.
    traceLifecycle: 'static',

    // Performance Monitoring Configuration
    tracesSampleRate: isDevelopment ? 1.0 : isStaging ? 0.3 : 0.1,

    // Memory and performance tracking
    tracePropagationTargets: [
      'localhost',
      /^https:\/\/nop\.commandcenter\.transmedics\.com/,
      /^https:\/\/stage\.nop\.commandcenter\.transmedics\.com/,
      /^https:\/\/dev\.nop\.commandcenter\.transmedics\.com/,
      /^https:\/\/old\.nop\.commandcenter\.transmedics\.com/,
    ],

    // Profiling for CPU monitoring (only in non-production)
    profileSessionSampleRate: isDevelopment ? 1.0 : 0,
    profileLifecycle: 'trace',

    // Session Replay sampling rates
    // Replays can become a major cost driver when Error volume is high.
    replaysSessionSampleRate: isProduction ? 0.01 : isStaging ? 0.05 : 0.0,
    replaysOnErrorSampleRate: isProduction ? 0.1 : isStaging ? 0.5 : 1.0,

    // Release tracking (using package version if available)
    release: import.meta.env.REACT_APP_VERSION || 'development',

    // Custom performance metrics for memory/GPU monitoring
    async beforeSend(event, _hint) {
      // Apply filtering + rate limiting first to avoid expensive enrichment work.
      let filteredEvent: Sentry.Event | null = customErrorFilter(event);
      if (!filteredEvent) {
        return null;
      }

      filteredEvent = rateLimitedErrorFilter(filteredEvent);
      if (!filteredEvent) {
        return null;
      }

      const e = filteredEvent as Sentry.ErrorEvent;

      // Add memory usage information
      if (performance.memory) {
        e.contexts = {
          ...e.contexts,
          memory: {
            used: performance.memory.usedJSHeapSize,
            total: performance.memory.totalJSHeapSize,
            limit: performance.memory.jsHeapSizeLimit,
          },
        };
      }

      // Add viewport dimensions
      const viewport = getViewportDimensions();
      e.contexts = {
        ...e.contexts,
        viewport: {
          width: viewport.width,
          height: viewport.height,
          devicePixelRatio: viewport.devicePixelRatio,
        },
      };

      // Add hashed user email if available
      const userEmail = await getUserEmail();
      if (userEmail) {
        try {
          const hashedEmail = await hashString(userEmail);
          e.user = {
            ...e.user,
            hash: hashedEmail, // Use hashed email as hash field
          };
          e.tags = {
            ...e.tags,
            hasUserEmail: 'true',
          };
        } catch (error) {
          console.error('Failed to hash user email:', error);
        }
      } else {
        e.tags = {
          ...e.tags,
          hasUserEmail: 'false',
        };
      }

      return e;
    },

    beforeBreadcrumb: customBreadcrumbFilter,

    // Transport configuration for reliability
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),

    // Additional options
    attachStacktrace: true,
    dataCollection: V10_DATA_COLLECTION_BASELINE,
    maxBreadcrumbs: 50,

    // Custom tags
    initialScope: {
      tags: {
        component: 'frontend',
        domain: location.hostname,
        viewportWidth: window.innerWidth.toString(),
        viewportHeight: window.innerHeight.toString(),
      },
    },
  });

  // Set an anonymous, stable user id (non-PII) so "Users" counts become meaningful.
  // This is overwritten later when authenticated user context is available.
  try {
    const anonId = getOrCreateAnonymousUserId();
    if (anonId) {
      Sentry.setUser({ id: anonId });
      Sentry.setTag('user.anonymous', 'true');
    }
  } catch {
    // Never break app on telemetry
  }

  /**
   * Enrich Sentry context with user email hash from AWS Amplify.
   * This allows identifying unique users without storing their actual email.
   */
  const enrichSentryUserContext = async () => {
    try {
      const authSession = await fetchAuthSession();
      const idToken = authSession.tokens?.idToken;

      if (idToken) {
        // Extract email from the ID token payload
        const payload = idToken.payload;
        const email =
          (payload?.email as string) || (payload?.preferred_username as string);

        if (email) {
          const emailHash = await hashString(email);

          Sentry.setUser({
            id: payload?.sub as string,
            // Use emailHash instead of raw email for privacy
            email: emailHash,
            username: emailHash,
            extra: {
              emailHash,
              cognitoSub: payload?.sub as string,
            },
          });

          // Add tag for filtering by email hash (first 12 chars for brevity)
          Sentry.setTag('user.emailHash', emailHash.substring(0, 12));

          Sentry.addBreadcrumb({
            message: 'User context enriched with email hash',
            category: 'auth',
            level: 'info',
            data: {
              emailHash: `${emailHash.substring(0, 12)}...`,
            },
          });
        }
      }
    } catch {
      // Silently fail if user is not authenticated
      // User context will be set when they authenticate
    }
  };

  // Attempt to enrich user context on initialization
  enrichSentryUserContext();

  // Monitor memory usage periodically
  if (isProduction && performance.memory) {
    setInterval(() => {
      const memoryUsage =
        performance.memory.usedJSHeapSize / performance.memory.jsHeapSizeLimit;

      // Alert if memory usage is above 80%
      if (memoryUsage > 0.8) {
        Sentry.addBreadcrumb({
          message: 'High memory usage detected',
          level: 'warning',
          data: {
            usagePercentage: Math.round(memoryUsage * 100),
            used: performance.memory.usedJSHeapSize,
            limit: performance.memory.jsHeapSizeLimit,
          },
        });
      }
    }, 30000); // Check every 30 seconds
  }
}
